import React, { useEffect, useState, useRef, useCallback } from "react";
import styles from './FastSetup.module.css';
import HeaderBar from '../HeaderBar/HeaderBar';
import { useLocation, useNavigate } from "react-router-dom";
import { fetchAgentDetailById, getAgentById } from "../../Store/apiStore";
import { useAgentStore } from "../../Store/agentDetailStore";
import decodeToken from "../../lib/decodeToken";
import { RetellWebClient } from "retell-client-js-sdk";
import Loader2 from "../Loader2/Loader2";
import PopUp from "../Popup/Popup";
import { markSetupStep } from "../../lib/setupScore";
import { captureOAuth } from "../../lib/integrationOAuth";
import { trackEvent } from "../../lib/analytics";
import { getVariant, EXPERIMENTS, VARIANT_B } from "../../lib/experiments";
import useBackTo from "../../lib/useBackTo";
import AnimatedButton2 from "../AnimatedButton/AnimatedButton2";
import { isNativeApp } from "../../utils/nativePermissions";
import { useGlobalCall, isMobileAppSurface } from "../../contexts/GlobalCallContext";
import { useOfferStore, bumpSessionCallCount, getSessionCallCount } from "../../Store/offerStore";
import { isAppSumoUser, isIndiaSite, isPythonBackend, startPythonTestCall } from "../../utils/apiBackend";
import KeepTalkingModal from "../GlobalCallBanner/KeepTalkingModal";
import CallWaveform from "../GlobalCallBanner/CallWaveform";

// Per-agent record of when the "Meet" popup last auto-opened, kept at MODULE
// scope (NOT sessionStorage). A module var resets on a full page reload, so a
// REFRESH re-opens the popup — but it persists across in-app (SPA) navigations,
// so navigating back to /fast-step does NOT reopen it. Keyed by agent so a
// freshly-built agent still gets its intro popup. Timestamp guards StrictMode's
// mount→unmount→remount (that remount is ms later, so it keeps the popup open).
const fastStepPopupShownAt = {};
const SAME_LANDING_WINDOW_MS = 3000;

// ── End-Call sign-off wait tuning ────────────────────────────────────────
// Retell `agent_stop_talking` har PAUSE pe fire hota hai — do sentences ke
// beech wali saans pe bhi. Isliye "pehla stop" ka matlab "bol chuka" NAHI
// hai; us par hang up karne se sign-off beech me kat jata hai.
// Agent ko tabhi finished maante hain jab wo LAGATAR itni der chup rahe:
const SIGNOFF_SILENCE_HOLD_MS = 1500;
// Inject ke baad itni der me bolna shuru nahi kiya → maan lo inject nahi
// pahuncha, bekaar me user ko silence me mat bithao.
const SIGNOFF_START_GRACE_MS = 4000;
// Total wait ka hard cap — stuck "talking" state kabhi hamesha ke liye na roke.
const SIGNOFF_MAX_WAIT_MS = 20000;

const FastSetup = () => {
    const {
        agentData,
        assignedNumbers,
        totalBookings,
        setAgentById,
        setCurrentAgentId,
        getAgentById,
    } = useAgentStore();
    const isDataLoading = !agentData?.agent?.agentName;

    const navigate = useNavigate();
    const location = useLocation();

    // ── App-wide call (Android/iOS only) ──────────────────────────────
    // On the native app the test call is auto-started here and handed to the
    // global provider so it survives route changes and shows the persistent
    // banner. On web, this stays dormant and the existing dialog flow runs.
    const {
        startGlobalCall,
        primeGlobalCall,
        endGlobalCall,
        injectContext,
        callStatus: globalCallStatus,
        isAgentTalking: globalAgentTalking,
    } = useGlobalCall();
    const isMobileApp = isMobileAppSurface();
    const globalAutoStartedRef = useRef(false);
    // Fires the one-time "resume the conversation" inject after satisfaction.
    const resumeInjectedRef = useRef(false);
    // Sales agent found the owner satisfied (promote) or triggered an offer →
    // collapse the "Meet Sophia" popup so the persistent banner takes over on
    // this SAME screen (no navigation).
    const forcedOffer = useOfferStore((s) => s.forcedOffer);
    const promoted = useOfferStore((s) => s.promoted);
    // Agent's prior call count (agent_calls_index) — fetched on mount so the
    // "first-ever call" Go Live gating survives a page reload.
    const priorCallCount = useOfferStore((s) => s.priorCallCount);
    const setPriorCallCount = useOfferStore((s) => s.setPriorCallCount);
    // silent_mode tool: user asked the agent to go quiet → close the popup, but
    // keep the call running (banner). silentNonce re-fires on repeat requests.
    const silentNonce = useOfferStore((s) => s.silentNonce);
    // "Can we please keep talking?" confirm before ending from the live widget.
    const [showEndConfirm, setShowEndConfirm] = useState(false);
    // Business-name tooltip (shows the full name on hover AND on tap/touch).
    const [showBizTip, setShowBizTip] = useState(false);
    const bizTipTimer = useRef(null);

    // While the tooltip is open, a click ANYWHERE closes it. (The opening tap
    // calls stopPropagation, so it won't self-close; this listener is added
    // after that tap has already finished.)
    useEffect(() => {
        if (!showBizTip) return undefined;
        const close = () => setShowBizTip(false);
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, [showBizTip]);

    const [name, setName] = useState(agentData?.agentName);
    const [isEditing, setIsEditing] = useState(false);
    const inputRef = useRef(null);
    const token = localStorage.getItem("token") || "";
    const agentID = sessionStorage.getItem("SelectAgentId");
    const agentBuisnesId = sessionStorage.getItem("SelectAgentBusinessId");
    const { id: userId } = decodeToken(token) || {};

    // Call Test States
    const [retellWebClient, setRetellWebClient] = useState(null);
    // Python flow (rexpt.in / port 3001): test call Retell ke bajaye Python
    // WebSocket se chalti hai. Handle yahan store hota hai taaki hang-up ho sake.
    const pythonCallRef = useRef(null);
    const [isCallActive, setIsCallActive] = useState(false);
    const [isCallConnected, setIsCallConnected] = useState(false);
    // Tracks whether at least one test call has fully completed or popup shown.
    // Used to swap the bottom CTA area into the post-call summary (Figma 9b).
    const [hasCompletedCall, setHasCompletedCall] = useState(() => {
        const key = agentID || "_";
        return sessionStorage.getItem(`fastStepPopupShown_${key}`) === "true";
    });
    const [callLoading, setCallLoading] = useState(false);

    // Fetch the agent's prior call count once on mount so the Go Live gating
    // knows whether this is a first-EVER call even after a page reload (the
    // per-session hasCompletedCall resets on reload). Endpoint is unauthed.
    useEffect(() => {
        if (!agentID) return;
        // Python flow (rexpt.in / port 3001): agent Node DB me nahi hai — call-count
        // (Node) skip karo, warna reload pe bekaar 404 jaata hai.
        if (isPythonBackend()) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/agent/call-count/${agentID}`
                );
                const data = await res.json();
                if (!cancelled && typeof data?.count === "number") {
                    setPriorCallCount(data.count);
                }
            } catch (_e) {
                /* ignore — falls back to session hasCompletedCall */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [agentID, setPriorCallCount]);

    // Web (local Retell) "agent is speaking" flag — drives the natural wave.
    // On native we use the global provider's isAgentTalking instead.
    const [webAgentTalking, setWebAgentTalking] = useState(false);
    const webAgentTalkingRef = useRef(false);
    // Native ka talking flag state hai (ref nahi), aur callbacks ke andar state
    // stale ho jati hai — isliye ek ref me mirror kar lete hain.
    const globalAgentTalkingRef = useRef(false);
    useEffect(() => {
        globalAgentTalkingRef.current = globalAgentTalking;
    }, [globalAgentTalking]);

    // Ek hi "abhi agent bol raha hai?" source — web aur native dono ke liye.
    // Isi ke upar End-Call ka sign-off wait chalta hai, taaki dono platform pe
    // behaviour BILKUL same rahe (koi alag branch nahi).
    const isAgentSpeakingNow = useCallback(
        () => (isMobileApp ? globalAgentTalkingRef.current : webAgentTalkingRef.current),
        [isMobileApp]
    );
    // Debounced version of "agent talking" — lingers briefly after speech stops
    // so tiny pauses between words don't kill the wave (natural, no flicker).
    const [waveOn, setWaveOn] = useState(false);
    const [loading, setLoading] = useState(false);
    const [callId, setCallId] = useState(null);
    const [isEnding, setIsEnding] = useState(false);

    // The "Meet" dialog auto-opens on the FIRST visit for an agent.
    // Reloads (refreshes) and in-app re-navigation will NOT re-open it because
    // fastStepPopupShown_[key] is saved in sessionStorage.
    const decideAutoOpenPopup = () => {
        const key = agentID || "_";
        // Arrived here from FastAgentDetails "Continue" → never auto-open the
        // "Meet" intro dialog; land straight on the ready summary. Also mark it
        // seen so a later reload/revisit doesn't pop it either.
        if (location.state?.suppressPopup) {
            try { sessionStorage.setItem(`fastStepPopupShown_${key}`, "true"); } catch (_e) { /* ignore */ }
            return false;
        }
        if (sessionStorage.getItem(`fastStepPopupShown_${key}`) === "true") {
            return false;
        }
        const now = Date.now();
        const last = fastStepPopupShownAt[key] || 0;
        if (!last) {
            fastStepPopupShownAt[key] = now;
            sessionStorage.setItem(`fastStepPopupShown_${key}`, "true");
            return true;
        }
        if (now - last < SAME_LANDING_WINDOW_MS) return true;
        return false;
    };
    // WHETHER the intro dialog should auto-open (session/timing decision — has
    // side effects, so compute ONCE). We then open it only AFTER the agent data
    // is ready (below), so the user never sees the popup flash with placeholder
    // text ("Sophia" / no business) that then fills in.
    const shouldAutoOpenRef = useRef(decideAutoOpenPopup());
    const autoOpenedRef = useRef(shouldAutoOpenRef.current);
    const [showCallDialog, setShowCallDialog] = useState(shouldAutoOpenRef.current);

    // Exit-animation plumbing: when showCallDialog flips to false we keep the
    // dialog mounted for one more beat so it can zoom out + float upward (the
    // ready view is revealed behind the blurred backdrop), then unmount.
    const [dialogRender, setDialogRender] = useState(shouldAutoOpenRef.current);
    const [dialogClosing, setDialogClosing] = useState(false);

    // Open the "Meet" intro dialog only once the agent's real data is loaded, so
    // it opens already-filled (name/business/avatar) instead of empty-then-fill.
    useEffect(() => {
        if (autoOpenedRef.current || !shouldAutoOpenRef.current) return;
        const dataReady = !!agentData?.agent?.agentName;
        if (!dataReady) return;
        autoOpenedRef.current = true;
        setShowCallDialog(true);
        setDialogRender(true);
    }, [agentData]);

    // In-app popup state — replaces native browser alert() for permission errors.
    // Mirrors the pattern in FastAgentDetails.js / Dashboard.js so the look
    // matches across screens (no jarring browser-default dialog).
    const [popupMessage, setPopupMessage] = useState("");
    const [popupType, setPopupType] = useState("failed");
    // Inline "call ended" note (no popup) — dialog ke andar hi dikhta hai.
    const [showCallEndedNote, setShowCallEndedNote] = useState(false);

    // Refs
    const isStartingRef = useRef(false);
    const isEndingRef = useRef(false);
    // Guards the End-Call sign-off flow (inject → wait for agent → hang up) so a
    // double-tap can't fire it twice.
    const farewellRunningRef = useRef(false);
    const micStream = useRef(null);
    const autoStartTriggered = useRef(false);
    const cleanupExecuted = useRef(false);
    const autoStopTimer = useRef(null);
    const clientRef = useRef(null);
    // GA4: test-call start time, to report duration_sec on end.
    const callStartTimeRef = useRef(null);
    const hasAutoCalledRef = useRef(false);
    const isAutoCallRef = useRef(false);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    const prevGlobalStatusRef = useRef(globalCallStatus);
    const isReloadingRef = useRef(false);
    const handleStartCallRef = useRef(null);
    // Set true if the web call actually dropped WHILE a reload prompt was up
    // (Chrome pauses the page → Retell's WebRTC can time out). On CANCEL we use
    // it to decide whether to silently reconnect. Reset when a call starts.
    const callEndedDuringReloadRef = useRef(false);

    // Check if any call action is in progress (mobile app global call OR local web call)
    const isCallInProgress = isMobileApp
        // "ended" is a TERMINAL state (call finished, banner lingers) — treat it
        // like idle so a restart isn't blocked. Only connecting/active/ending count.
        ? (globalCallStatus !== "idle" && globalCallStatus !== "ended")
        : (isCallActive || isCallConnected || callLoading || isEndingRef.current);

    // Capture OAuth params only; the provider popup now closes from /fast-agent-detail (after pay/skip), not here.
    useEffect(() => {
        captureOAuth(window.location.search);
    }, [token]);

    /*
     * Back → /fast-agent-detail.
     *
     * SENTINEL kyun: pehle yahan sirf popstate suna jaata tha aur seedha
     * navigate() hota tha. Wo race haar jaata hai — back dabte hi React Router
     * apna handler pehle chala kar route badal deta hai, aur user peeche wali
     * screen (aksar /business-step) pe pahunch jaata tha. Isi wajah se ye "kabhi
     * kabhi" galat jagah le jaata tha.
     *
     * useBackTo ek duplicate history entry rakhta hai (aur har pop ke baad
     * dobara bhar deta hai), isliye Router ko koi change dikhta hi nahi aur
     * hamara navigate hamesha jeet'ta hai.
     */
    useBackTo("/fast-agent-detail");

    /*
     * Back par test call band bhi karni hai — call screen chhodne ke baad wo
     * chup-chaap chalti nahi rehni chahiye. Ye useBackTo se alag rakha hai
     * kyunki iska apna guard hai: plans popup khula ho to back USE consume
     * karta hai (GlobalOfferModal khud band hota hai), call nahi kat'ni chahiye.
     */
    useEffect(() => {
        const handlePopState = () => {
            if (useOfferStore.getState().forcedOffer === "plans") return;
            try {
                if (isMobileApp) {
                    endGlobalCall();
                } else if (clientRef.current) {
                    clientRef.current.stopCall();
                }
            } catch (_e) {
                /* best-effort */
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [isMobileApp, endGlobalCall]);

    // GA4: "Meet [agent]" ready screen shown = agent build complete.
    // Fire ONLY when a build actually just finished — i.e. the agentBuildStart
    // marker (set in BusinessStep right after createAgent) is present. Without
    // this gate the event fired on EVERY visit to this screen, and a marker-less
    // visit produced params {build_seconds: undefined} → key "{}" which the
    // one-shot guard then blocked forever. The marker is removed BEFORE firing so
    // a refresh / StrictMode re-mount can't re-fire it. build_id (the start
    // timestamp) makes each real build a UNIQUE event so the guard never drops a
    // second build that happened to take the same number of seconds.
    useEffect(() => {
        const startedAt = parseInt(sessionStorage.getItem("agentBuildStart") || "0", 10);
        if (!startedAt) return; // no build just completed → don't fire on plain revisits
        sessionStorage.removeItem("agentBuildStart"); // consume first → no re-fire on remount
        const buildSeconds = Math.round((Date.now() - startedAt) / 1000);
        trackEvent("agent_build_complete", { build_seconds: buildSeconds, build_id: startedAt });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Best-effort cleanup ONLY — NO "Leave site?" confirm dialog.
    // We do NOT show the browser beforeunload prompt anymore (it popped up on
    // navigation/checkout during a call, which was annoying). We only listen to
    // `pagehide` (fires when the page is ACTUALLY unloading — reload / close /
    // real navigation) to stop the call + release the mic so nothing lingers.
    // pagehide never shows a dialog, so the user is never asked to confirm.
    useEffect(() => {
        if (location.pathname !== "/fast-step") return undefined;

        const callIsLive = () =>
            isCallActive || isCallConnected || callLoading || (globalCallStatus && globalCallStatus !== "idle");

        const handlePageHide = () => {
            if (!callIsLive()) return;
            try {
                if (autoStopTimer.current) {
                    clearTimeout(autoStopTimer.current);
                    autoStopTimer.current = null;
                }
                if (clientRef.current) {
                    clientRef.current.stopCall();
                }
                if (micStream.current) {
                    micStream.current.getTracks().forEach((t) => t.stop());
                    micStream.current = null;
                }
                if (isMobileApp) {
                    endGlobalCall();
                }
            } catch (_e) {
                // Best-effort only; ignore failures during teardown.
            }
        };

        window.addEventListener("pagehide", handlePageHide);
        return () => {
            window.removeEventListener("pagehide", handlePageHide);
        };
    }, [isCallActive, isCallConnected, callLoading, globalCallStatus, isMobileApp, endGlobalCall, location.pathname]);

    // Handle end call function
    const handleEndCall = useCallback(async () => {
        // Native app: the dialog drives the GLOBAL call, so end that (not the
        // unused local client). Then close the dialog to reveal the 9b summary.
        if (isMobileApp) {
            endGlobalCall();
            setShowCallDialog(false);
            setHasCompletedCall(true);
            return;
        }

        if (isEndingRef.current) {
            // console.log("Already ending call, skipping...");
            return;
        }

        // console.log("End call initiated");
        isEndingRef.current = true;
        setIsEnding(true);

        // Clear the auto-stop timer if it exists
        if (autoStopTimer.current) {
            clearTimeout(autoStopTimer.current);
            autoStopTimer.current = null;
            // console.log("Auto-stop timer cleared in handleEndCall");
        }

        // Python flow: koi Retell client nahi — Python WebSocket call band karo.
        if (isPythonBackend() && pythonCallRef.current) {
            try { pythonCallRef.current.hangUp(); } catch (_e) { /* ignore */ }
            pythonCallRef.current = null;
            setIsCallActive(false);
            setIsCallConnected(false);
            setCallLoading(false);
            if (callStartTimeRef.current) {
                const durationSec = Math.round((Date.now() - callStartTimeRef.current) / 1000);
                callStartTimeRef.current = null;
                trackEvent("test_call_complete", { duration_sec: durationSec });
            }
            setHasCompletedCall(true);
            setShowCallEndedNote(true);
            setShowCallDialog(false);
            isEndingRef.current = false;
            setIsEnding(false);
            return;
        }

        const client = clientRef.current || retellWebClient;

        if (client) {
            try {
                // console.log("Stopping call via retellWebClient...");
                await client.stopCall();
                // console.log("Call stopped successfully");
                setIsCallActive(false);
                setIsCallConnected(false);
                setCallLoading(false);

                // GA4: activation-preview — user heard the AI (free). Only fire
                // if a call actually started (avoids firing on a no-op end).
                if (callStartTimeRef.current) {
                    const durationSec = Math.round(
                        (Date.now() - callStartTimeRef.current) / 1000,
                    );
                    callStartTimeRef.current = null;
                    trackEvent("test_call_complete", { duration_sec: durationSec });
                }

                // Mark the test-call experience as completed so the UI can
                // transition to the Figma 9b post-call summary view.
                setHasCompletedCall(true);
                // Inline "call ended" note 9b view pe dikhega.
                setShowCallEndedNote(true);
                // Dismiss the call dialog so the page reveals 9b.
                setShowCallDialog(false);
            } catch (err) {
                console.error("Error ending call:", err);
            } finally {
                setTimeout(() => {
                    isEndingRef.current = false;
                    setIsEnding(false);
                    console.log("End call state reset");
                }, 1000);
            }
        } else {
            console.error("retellWebClient not available");
            isEndingRef.current = false;
            setIsEnding(false);
            // No client to clean up, but still close the dialog so the
            // user isn't stuck on it.
            setShowCallDialog(false);
        }
    }, [retellWebClient, isMobileApp, endGlobalCall]);

    // Forced dialog dismissal (X button, overlay click). Always ensures
    // the underlying call is stopped so we don't leave a Retell session
    // running in the background. Refresh / unload is covered by the
    // beforeunload listener below.
    //
    // Either path falls through to 9b — even if the user dismissed
    // without testing — so they don't end up staring at a blank page.
    // 9b's "Test Sophia again" link re-opens this dialog, so closing
    // prematurely is recoverable.
    const handleDialogClose = useCallback(() => {
        const callIsLive = isCallActive || isCallConnected || callLoading || (globalCallStatus && globalCallStatus !== "idle");
        if (callIsLive) {
            // During an active call, closing is blocked so popup stays open & call stays connected
            return;
        }
        setHasCompletedCall(true);
        setShowCallDialog(false);
    }, [isCallActive, isCallConnected, callLoading, globalCallStatus]);

    // Function to start call with pre-fetched access token (AUTO CALL)
    const startAutoCallWithToken = useCallback(async (tokenOrData, maybeCallId, maybeTransport, maybeIceServers) => {
        if (autoStartTriggered.current || !retellWebClient || hasAutoCalledRef.current) {
            return;
        }

        let accessToken = tokenOrData;
        let callId = maybeCallId;
        let transport = maybeTransport;
        let iceServers = maybeIceServers;

        if (typeof tokenOrData === "object" && tokenOrData !== null) {
            accessToken = tokenOrData.access_token || tokenOrData.accessToken;
            callId = tokenOrData.call_id || tokenOrData.callId;
            transport = tokenOrData.transport;
            iceServers = tokenOrData.ice_servers || tokenOrData.iceServers;
        }

        autoStartTriggered.current = true;
        hasAutoCalledRef.current = true;
        isAutoCallRef.current = true; // Mark as auto-call

        // Request microphone access
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });
            micStream.current = stream;
        } catch (err) {
            console.error("Microphone access denied:", err);
            setPopupMessage("Microphone access is required to test the agent.");
            setPopupType("failed");
            autoStartTriggered.current = false;
            hasAutoCalledRef.current = false;
            isAutoCallRef.current = false;
            return;
        }

        if (isStartingRef.current || isCallActive) {
            console.error("Call already in progress.");
            return;
        }

        isStartingRef.current = true;
        setCallLoading(true);

        try {
            await retellWebClient.startCall({
                accessToken,
                callId,
                transport,
                iceServers,
                emitRawAudioSamples: true,
            });
            callStartTimeRef.current = Date.now();
            setCallId(callId);
            // console.log(" Auto-call started with callId:", callId);
        } catch (err) {
            console.error(" Error starting auto call:", err);
            alert("Failed to start call. Please try again.");
            setCallLoading(false);
            isStartingRef.current = false;
            autoStartTriggered.current = false;
            hasAutoCalledRef.current = false;
            isAutoCallRef.current = false;
        } finally {
            isStartingRef.current = false;
        }
    }, [retellWebClient, isCallActive]);

    // Initialize Retell Web Client
    useEffect(() => {
        // console.log("🔧 Initializing Retell Web Client...");/
        const client = new RetellWebClient();
        setRetellWebClient(client);
        clientRef.current = client;

        client.on("call_started", () => {
            // console.log(" Call started - Agent speaking now!");
            setIsCallActive(true);
            setIsCallConnected(true);
            setCallLoading(false);
            // Fresh call up → clear any stale "dropped during reload" flag.
            callEndedDuringReloadRef.current = false;

            // Auto-stop ONLY for auto-initiated calls (not manual calls)
            if (isAutoCallRef.current && !autoStopTimer.current) {
                // console.log(" Setting 6-second auto-stop timer for AUTO-CALL...");

                autoStopTimer.current = setTimeout(async () => {
                    // console.log("Auto-stop timer triggered!");

                    if (isEndingRef.current) {
                        // console.log("Already ending, skipping auto-stop");
                        return;
                    }

                    // console.log(" Executing auto-stop");
                    isEndingRef.current = true;
                    setIsEnding(true);

                    try {
                        if (clientRef.current) {
                            await clientRef.current.stopCall();
                            // console.log(" Auto-stop completed");
                            setIsCallActive(false);
                            setIsCallConnected(false);
                            setCallLoading(false);
                        }
                    } catch (err) {
                        console.error(" Error in auto-stop:", err);
                    } finally {
                        setTimeout(() => {
                            isEndingRef.current = false;
                            setIsEnding(false);
                            isAutoCallRef.current = false; // Reset auto-call flag
                            // console.log("Auto-stop state reset");
                        }, 1000);
                    }
                }, 10000); // 10 seconds
            } else {
                // console.log("ℹ Manual call - NO auto-stop timer");
            }
        });

        // Agent voice-activity → drives the natural waveform (only bounces
        // while the agent is actually speaking).
        client.on("agent_start_talking", () => {
            setWebAgentTalking(true);
            webAgentTalkingRef.current = true;
        });
        client.on("agent_stop_talking", () => {
            setWebAgentTalking(false);
            webAgentTalkingRef.current = false;
        });

        client.on("call_ended", () => {
            // ALWAYS reset the core call-liveness flags/locks first — even if a
            // reload prompt is up. Otherwise a guarded early-return would leave
            // isCallActive/isCallConnected stuck TRUE, so isCallInProgress stays
            // true and "Test again" / Make Web Call can never restart the call.
            if (autoStopTimer.current) {
                clearTimeout(autoStopTimer.current);
                autoStopTimer.current = null;
            }
            setWebAgentTalking(false);
            webAgentTalkingRef.current = false;
            setIsCallActive(false);
            setIsCallConnected(false);
            setCallLoading(false);
            isEndingRef.current = false;
            setIsEnding(false);
            isStartingRef.current = false; // clear start-lock so a restart isn't blocked
            isAutoCallRef.current = false;

            // Reload prompt is up → the browser dropped the call while paused.
            // Remember it (so a CANCEL can reconnect) but skip the "call ended"
            // summary UI — the reload/cancel logic owns what the screen shows.
            if (isReloadingRef.current) {
                callEndedDuringReloadRef.current = true;
                return;
            }

            // Mark post-call summary view eligible (Figma 9a)
            setHasCompletedCall(true);
            // Reveal the offer pay buttons now the (local/web) call has ended.
            try {
                useOfferStore.getState().markCallEnded();
                // Reload-safe fallback: remember this agent has now had a call.
                bumpSessionCallCount(agentID);
            } catch (_e) {
                /* ignore */
            }
            // Inline "call ended" banner thodi der ke liye dikhao (no popup).
            setShowCallEndedNote(true);

            // Mark Setup Score testCallCompleted (15 pts). Fire-and-forget via
            // the shared helper — backend is idempotent so re-fires are safe.
            markSetupStep("testCallCompleted", agentID);

            // Clean up session storage
            if (!cleanupExecuted.current) {
                sessionStorage.removeItem('autoCallAccessToken');
                sessionStorage.removeItem('autoCallCallId');
                cleanupExecuted.current = true;
                // console.log(" Session storage cleaned");
            }

            // Stop microphone
            if (micStream.current) {
                micStream.current.getTracks().forEach(track => track.stop());
                micStream.current = null;
            }
        });

        client.on("error", (error) => {
            // Reload prompt up → treat like a drop-during-reload (reconnect on cancel).
            if (isReloadingRef.current) {
                callEndedDuringReloadRef.current = true;
                return;
            }
            console.error(" Retell error:", error);

            if (autoStopTimer.current) {
                clearTimeout(autoStopTimer.current);
                autoStopTimer.current = null;
            }

            setWebAgentTalking(false);
            webAgentTalkingRef.current = false;
            setIsCallActive(false);
            setIsCallConnected(false);
            setCallLoading(false);
            isEndingRef.current = false;
            setIsEnding(false);
            isAutoCallRef.current = false;

            if (!cleanupExecuted.current) {
                sessionStorage.removeItem('autoCallAccessToken');
                sessionStorage.removeItem('autoCallCallId');
                cleanupExecuted.current = true;
            }
        });

        return () => {
            if (autoStopTimer.current) {
                clearTimeout(autoStopTimer.current);
                autoStopTimer.current = null;
            }

            if (micStream.current) {
                if (!isMobileApp || (globalCallStatus !== "active" && globalCallStatus !== "connecting")) {
                    micStream.current.getTracks().forEach(track => track.stop());
                }
            }

            clientRef.current = null;
        };
    }, []);

    // Drive the dialog's mount/exit-animation lifecycle off showCallDialog.
    useEffect(() => {
        if (showCallDialog) {
            setDialogRender(true);
            setDialogClosing(false);
            return;
        }
        if (!dialogRender) return; // already unmounted
        // Dialog just closed → play the zoom-out-upward exit, then unmount.
        setDialogClosing(true);
        const t = setTimeout(() => {
            setDialogClosing(false);
            setDialogRender(false);
        }, 650);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showCallDialog]);

    // "Call ended" inline note ko thodi der baad apne aap hide kar do.
    useEffect(() => {
        if (!showCallEndedNote) return;
        const t = setTimeout(() => setShowCallEndedNote(false), 4000);
        return () => clearTimeout(t);
    }, [showCallEndedNote]);

    /*
     * ─── FUTURE-SCOPE: AUTO-START ON MOUNT ──────────────────────────────
     * Originally this effect kicked off the test call IMMEDIATELY when the
     * /fast-step screen mounted (using accessToken + callId passed via
     * location.state from /business-step Step 3 on agent creation).
     *
     * Per the Figma 9 redesign, the user must explicitly tap the green
     * "Make Web Call Now" button to start the call — no auto-start. So this
     * block is intentionally commented out.
     *
     * To RE-ENABLE auto-start in the future:
     *   1. Uncomment the useEffect below.
     *   2. Make sure /business-step Step 3 still populates location.state
     *      with { accessToken, callId } when navigating to /fast-step.
     *      (As of now the upstream code still sets these — only the
     *      consumer here is disabled.)
     *   3. Decide whether you want to hide the green CTA when auto-call is
     *      live (or keep it as a "Test again" trigger).
     *
     * Search-tag for grep: AUTO_START_CALL_DISABLED
     * ────────────────────────────────────────────────────────────────────
     */
    // useEffect(() => {
    //     const locationState = location.state;
    //     const hasStateTokens = locationState?.accessToken && locationState?.callId;
    //
    //     if (hasStateTokens && retellWebClient && !hasAutoCalledRef.current) {
    //         // Immediately clear the state to prevent re-triggering
    //         window.history.replaceState({}, document.title);
    //
    //         // Start the call
    //         startAutoCallWithToken(locationState.accessToken, locationState.callId);
    //
    //         // Clean up session storage immediately
    //         sessionStorage.removeItem('autoCallAccessToken');
    //         sessionStorage.removeItem('autoCallCallId');
    //     }
    // }, [retellWebClient, startAutoCallWithToken, location.state]);

    const handleEditClick = () => {
        if (isCallInProgress) return;
        setIsEditing(true);
        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    };

    // Close editing mode if a call becomes active/in progress
    useEffect(() => {
        if (isCallInProgress && isEditing) {
            setIsEditing(false);
        }
    }, [isCallInProgress, isEditing]);

    const handleBlur = async () => {
        setIsEditing(false);

        const originalName = agentData?.agent?.agentName;

        // Only hit API if name actually changed
        if (name && name !== originalName) {
            setLoading(true);
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/agentV2/updateAgent`,
                    {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            agent_id: agentData?.agent?.agent_id,
                            agentName: name,
                        }),
                    }
                );

                if (!res.ok) {
                    console.error("Failed to update agent name");
                    setName(originalName); // revert on failure
                    return;
                }

                const data = await res.json();
                console.log("Agent name updated:", data);

                // Zustand store update with new name
                setAgentById(agentID, {
                    ...agentData,
                    agent: {
                        ...agentData?.agent,
                        agentName: name,
                    }
                }, assignedNumbers, totalBookings);

            } catch (err) {
                console.error("Error updating agent name:", err);
                setName(originalName);
            }
        }
    };

    const getAgentDetailsAndBookings = async () => {
        const agentDetails = {
            agentId: agentID,
            bussinesId: agentBuisnesId
        };
        const cached = getAgentById(agentID);
        if (cached) {
            setCurrentAgentId(agentID);
        }
        try {
            // Python case bhi fetchAgentDetailById khud handle karta hai
            // (apiStore → Python-aware), response Node jaisa shape me aata hai.
            const response = await fetchAgentDetailById(agentDetails);
            const agentInfo = response?.data;
            let numbersArray = [];
            let total = 0;
            setAgentById(agentDetails.agentId, agentInfo, numbersArray, total);
            setName(agentInfo?.agent?.agentName);
        } catch (err) {
            console.error("Failed to fetch data", err.response || err.message || err);
        }
    };

    useEffect(() => {
        getAgentDetailsAndBookings();
    }, []);

    // Manual start call function (NOT auto-call)
    const handleStartCall = async () => {
        handleStartCallRef.current = handleStartCall;
        // Native app: the dialog's "Make Web Call Now" must NOT start a second,
        // local call — the global call is already auto-starting. Delegate to the
        // global provider instead (no-ops if a global call is already live).
        if (isMobileApp) {
            if (globalCallStatus === "idle" || globalCallStatus === "ended") {
                startGlobalCall({
                    agentId: agentData?.agent?.agent_id,
                    agentName: agentDisplayName,
                    agentAvatar: agentData?.agent?.avatar || agentData?.agent?.agentAvatar || "/svg/receptionist_profile.png",
                    businessName: businessNameDisplay,
                });
            }
            return;
        }

        if (isCallInProgress) return;

        // Nayi call shuru — purana "call ended" note hata do.
        setShowCallEndedNote(false);

        // This is a MANUAL call, NOT auto-call
        isAutoCallRef.current = false;

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });
            micStream.current = stream;
            trackEvent("test_call_start", { mic_permission: "granted" });
        } catch (err) {
            trackEvent("test_call_start", { mic_permission: "denied" });
            console.error("Microphone access denied:", err);
            // Use in-app PopUp instead of native browser alert().
            setPopupMessage("Microphone access is required to test the agent.");
            setPopupType("failed");
            return;
        }

        // ── Python flow: Retell ke bajaye Python WebSocket se test call ──
        // (isPythonBackend() = domain rexpt.in / port 3001). Same switch jo
        // agent-create pe use hota hai.
        if (isPythonBackend()) {
            // Guard against a SECOND concurrent Python call. isCallActive is still
            // false during the "connecting" window, so a fast double-trigger (or a
            // React re-render) could start call #2 while call #1 was mid-connect —
            // then two calls ran at once and their onStatus("ended"/"connected")
            // raced, leaving the "End Call" button stuck red. pythonCallRef being
            // set means a call already exists, so bail.
            if (isStartingRef.current || isCallActive || pythonCallRef.current || !agentData) {
                console.error("Agent details not ready or a call is already running.");
                return;
            }
            // Mic upar le liya; Python call apna hi getUserMedia karta hai, is
            // liye ye probe wala stream band kar do (double mic na khule).
            try { micStream.current?.getTracks().forEach((t) => t.stop()); } catch (_e) { /* ignore */ }

            isStartingRef.current = true;
            setCallLoading(true);
            markSetupStep("testCallCompleted", agentID);

            const agentId = agentData?.agent?.agent_id;
            pythonCallRef.current = startPythonTestCall(agentId, {
                onStatus: (s) => {
                    if (s === "connected") {
                        setIsCallActive(true);
                        setIsCallConnected(true);
                        setCallLoading(false);
                        callStartTimeRef.current = Date.now();
                    } else if (s === "ended" || s === "error") {
                        console.log("[pyCall] onStatus ended → resetting all call state");
                        setIsCallActive(false);
                        setIsCallConnected(false);
                        setCallLoading(false);
                        setIsEnding(false);
                        isEndingRef.current = false;
                        isStartingRef.current = false;
                        // The button also stays red while globalCallStatus !== "idle"
                        // (see isCallInProgress / dispCallConnected). End the global
                        // call too so the header banner + button reset — no-op if it
                        // was never started (web flow). This is what kept the button
                        // red after a clean Python call end.
                        try { endGlobalCall(); } catch (_e) { /* ignore */ }
                        // Clear the ref so the guard above lets a NEW call start,
                        // and so a stale instance can't keep the button active.
                        pythonCallRef.current = null;
                        if (s === "error") {
                            setPopupMessage("Failed to start call. Please try again.");
                            setPopupType("failed");
                        } else {
                            // The agent ended the call (caller asked to disconnect):
                            // close the popup and show the post-call summary — the
                            // same as the user pressing End Call (handleEndCall).
                            setHasCompletedCall(true);
                            setShowCallEndedNote(true);
                            setShowCallDialog(false);
                        }
                    }
                },
            });
            isStartingRef.current = false;
            return;
        }

        if (isStartingRef.current || isCallActive || !retellWebClient || !agentData) {
            console.error("RetellWebClient or agent details not ready.");
            return;
        }

        isStartingRef.current = true;
        setCallLoading(true);

        // Setup Score: testCallCompleted (15 pts). Fire on the user's
        // explicit intent to test (button tap) — at this point mic
        // permission has already been granted and we're committing to
        // create the web call. Helper is idempotent so the call_ended
        // handler also calling it later is harmless.
        markSetupStep("testCallCompleted", agentID);

        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/agent/create-web-call`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    // rexpt_test_call: owner ki apni test call — backend live-monitoring push skip karta hai
                    body: JSON.stringify({ agent_id: agentData?.agent?.agent_id, metadata: { rexpt_test_call: "true" } }),
                }
            );

            if (res.status === 403) {
                if (agentData?.agent?.agentPlan === "free") {
                    alert("Your Agent Plan has been exhausted. To continue, please upgrade your plan.");
                } else {
                    alert("Your Agent Plan has been exhausted. To continue, please enable Pay As You Go.");
                }
                setCallLoading(false);
                isStartingRef.current = false;
                return;
            }

            const data = await res.json();
            await retellWebClient.startCall({
                accessToken: data.access_token,
                callId: data.call_id,
                transport: data.transport,
                iceServers: data.ice_servers,
                emitRawAudioSamples: true,
            });
            callStartTimeRef.current = Date.now();
            setCallId(data?.call_id);
            console.log("Manual call started");
        } catch (err) {
            console.error("Error starting call:", err);
            alert("Failed to start call. Please try again.");
            setCallLoading(false);
            isStartingRef.current = false;
        } finally {
            isStartingRef.current = false;
        }
    };

    const handleGetNumber = () => {
        if (isCallInProgress) return;
        navigate('/get-number', { replace: true });
    };

    // Figma 9a primary CTA — the A/B SPLIT POINT.
    //
    //   variant A (control) → /recommended-plan      ← aaj ka flow, unchanged
    //   variant B           → /receptionist-number   ← naya number-first flow
    //
    // Variant backend assign karta hai aur user pe LOCK ho jaata hai (ek baar
    // likha, phir kabhi overwrite nahi), isliye same user ko har device aur har
    // reload pe wahi flow milta hai. getVariant() kabhi random nahi karta —
    // value na mile to "A", yaani purana tested rasta.
    const handleSeePlans = () => {
        const variant = getVariant(EXPERIMENTS.GO_LIVE_FLOW);
        // India site (.in) goes to the PLANS screen. Its own plans are shown
        // there, so it must skip the number-first route even when the selected
        // flow is Variant B.
        //
        // /recommended-plan already resolves currency from the visitor's country,
        // so the India plans appear without any change to that screen.
        // AppSumo buyers are the exception on BOTH sites: they have already paid
        // for a lifetime deal and hold their minutes in the credit pools, so a
        // plans screen has nothing to sell them. Their destination follows the
        // selected flow variant.
        const indiaSite = isIndiaSite();
        const wantsPlansScreen = indiaSite && !isAppSumoUser();
        const destination =
            !wantsPlansScreen && variant === VARIANT_B
                ? "/receptionist-number"
                : "/recommended-plan";

        // GA4: "Go Live with <agent>" pressed on the agent-ready screen.
        // variant param se hi A vs B ka funnel compare hoga. india_site is sent
        // too, otherwise .in traffic reads as "variant B" while never having
        // taken the B route — which would quietly corrupt the A/B comparison.
        trackEvent("go_live_clicked", {
            screen: "agent_ready",
            variant,
            india_site: indiaSite,
        });
        navigate(destination, { replace: true });
    };

    // Figma 9b "Test Sophia again" — start the call directly (no popup) on BOTH
    // platforms, so the ready summary flips straight into the live widget
    // (waveform pill + red End Call) shown inline.
    const handleTestSophiaAgain = () => {
        // Native app: re-run the call through the global provider/banner. Allow
        // starting from "idle" OR "ended" (a prior call's terminal state) —
        // startGlobalCall itself supports both.
        if (isMobileApp) {
            if (globalCallStatus !== "idle" && globalCallStatus !== "ended") return;
            startGlobalCall({
                agentId: agentData?.agent?.agent_id,
                agentName: agentDisplayName,
                agentAvatar: agentData?.agent?.avatar || agentData?.agent?.agentAvatar || "/svg/receptionist_profile.png",
                businessName: businessNameDisplay,
            });
            return;
        }
        // Web: start the local call inline (no dialog re-open). handleStartCall
        // requests mic + creates the web call; call_started flips isCallConnected
        // so the ready summary shows the live waveform pill + red End Call.
        // Clear any stuck reload guard first — a manual restart is definitely
        // NOT a reload, and a lingering guard would keep the call state stuck.
        isReloadingRef.current = false;
        if (isCallInProgress) return;
        autoStartTriggered.current = false;
        hasAutoCalledRef.current = false;
        cleanupExecuted.current = false;
        handleStartCall();
    };

    // Red "End Call" tap on the live widget. Before showing the keep-talking
    // modal, inject a live-context nudge (update-live) so the agent asks the
    // caller WHY they're ending / what went wrong — one last retention beat.
    // Mobile only: injectContext targets the GLOBAL call (the live one on native).
    const handleEndCallClick = () => {
        if (isMobileApp) {
            injectContext(
                "CONTEXT: The customer just tapped the End Call button. Do NOT end the call yourself and do NOT say goodbye. In ONE short, warm, empathetic sentence, ask them why they'd like to end the call and whether something went wrong, so you can help. Keep it brief and never pushy."
            );
        }
        setShowEndConfirm(true);
    };

    // Agent ka poora bola hua sentence khatam hone ka wait — web aur native
    // dono ke liye YAHI ek function chalta hai.
    //
    // Key baat: Retell/provider ka "stop talking" har PAUSE pe fire hota hai —
    // do sentences ke beech wali saans pe bhi. Isliye pehle stop par hang up
    // karna = sign-off beech me kaat dena. Hum tabhi finished maante hain jab
    // silence LAGATAR SIGNOFF_SILENCE_HOLD_MS tak tik jaye; beech me agent
    // dobara bola to window reset ho jati hai.
    const waitForAgentToFinishSpeaking = useCallback(
        () =>
            new Promise((resolve) => {
                const startTime = performance.now();
                // Tap ke waqt agent already bol raha ho to usi ko start maano.
                let hasStartedTalking = isAgentSpeakingNow();
                let silenceSince = null;

                const check = () => {
                    const now = performance.now();

                    // Hard cap — stuck "talking" state kabhi hamesha na roke.
                    if (now - startTime >= SIGNOFF_MAX_WAIT_MS) return resolve();

                    if (isAgentSpeakingNow()) {
                        // Bol raha hai (ya pause ke baad phir se shuru) →
                        // silence window reset.
                        hasStartedTalking = true;
                        silenceSince = null;
                    } else if (hasStartedTalking) {
                        // Chup hai — par ye sentence ke beech ka pause bhi ho
                        // sakta hai. Tabhi finish maano jab silence tik jaye.
                        if (silenceSince === null) silenceSince = now;
                        if (now - silenceSince >= SIGNOFF_SILENCE_HOLD_MS) return resolve();
                    } else if (now - startTime >= SIGNOFF_START_GRACE_MS) {
                        // Muh hi nahi khola — inject shayad pahuncha hi nahi.
                        // User ko poore 20s silence me mat bithao.
                        return resolve();
                    }

                    setTimeout(check, 100);
                };

                check();
            }),
        [isAgentSpeakingNow]
    );

    // The Meet-card "End Call Now" tap: give the agent a warm sign-off (via a
    // live-context inject) so it says "I'm always here, reach out anytime you
    // need help", WAIT for it to finish speaking, THEN end the call — which also
    // closes this popup. Runs ONLY from the End Call button; an intent-based
    // popup close (X / overlay → handleDialogClose) never triggers this.
    const handleEndCallWithFarewell = useCallback(async () => {
        if (isEndingRef.current || farewellRunningRef.current) return;
        farewellRunningRef.current = true;

        // [FIX: python-call-hangup-oneclick] Python flow me injectContext kaam
        // nahi karta (koi live-call id / update-live-call nahi) — yani agent
        // goodbye bol hi nahi sakta — aur waitForAgentToFinishSpeaking Python me
        // atak jaata tha, is liye pehli baar red button dabane pe call cut hi
        // nahi hoti thi (dobara dabana padta tha). Node ki tarah ek click me
        // cut karne ke liye Python me seedha hang up karo — farewell skip.
        if (isPythonBackend()) {
            setShowCallDialog(false);
            setHasCompletedCall(true);
            handleEndCall();
            farewellRunningRef.current = false;
            return;
        }

        // 1) Warm sign-off inject so the agent says goodbye before we hang up.
        try {
            injectContext("CONTEXT: The customer is ending the call. Do NOT ask any question or try to keep them on the line. Respond warmly and naturally in ONE short sentence. Say: “Oops! No worries, I’m always here for you, so feel free to reach out anytime you need help.” Then let the call end."            );
        } catch (_) { /* inject is best-effort */ }

        // 2) Close the popup RIGHT AWAY (reveal the summary) — the call stays LIVE
        //    so the agent can still speak the sign-off.
        setShowCallDialog(false);
        setHasCompletedCall(true);

        // 3) Give the inject time to reach the agent + start talking (LLM+TTS is
        //    1-3s), THEN wait for it to finish the full sign-off. Only after that
        //    do we actually end the call. graceMs is generous so we don't hang up
        //    before the agent has even started; maxMs caps the total wait.
        await new Promise((r) => setTimeout(r, 1500));

        // Web ho ya native — ek hi wait, ek hi rule.
        await waitForAgentToFinishSpeaking();

        // 4) End the call now.
        handleEndCall();
        farewellRunningRef.current = false;
    }, [injectContext, waitForAgentToFinishSpeaking, handleEndCall]);

    const handleGoDashboard = async () => {
        if (isCallInProgress || dashboardLoading) return;

        // GA4: "Explore Dashboard First" pressed on the agent-ready screen
        // (skipped Go Live for now → dashboard). Web-only button.
        trackEvent("explore_dashboard_clicked", { screen: "agent_ready" });

        setDashboardLoading(true);

        // try {
        //     await fetch(`${process.env.REACT_APP_API_BASE_URL}/notifications/fast-setup-abandon`, {
        //         method: "POST",
        //         headers: { "Content-Type": "application/json" },
        //         body: JSON.stringify({
        //             userId,
        //             agentId: agentID,
        //         }),
        //     });
        // } catch (e) {
        //     console.error("abandon email trigger failed:", e);
        // } finally {
        //     setDashboardLoading(false);
        //     navigate("/fast-agent-detail", { replace: true });
        // }
        setDashboardLoading(false);
        navigate("/fast-agent-detail", { replace: true });
    };

    /*
     * Display copy for the business name. We fall back through three sources
     * because the API shape has drifted across versions, and finally to a
     * generic placeholder. We cap at 40 characters so very long business names
     * don't blow out the headline layout (per Figma — long names show "...").
     */
    const businessNameRaw =
        agentData?.businessName?.businessName ||
        agentData?.business?.businessName ||
        agentData?.agent?.businessName ||
        agentData?.businessName ||
        "your business";
    const businessNameDisplay =
        typeof businessNameRaw === "string"
            ? businessNameRaw.substring(0, 40)
            : "your business";

    // Same fallback chain for the agent display name (used in both views).
    const agentDisplayName = name || agentData?.agent?.agentName || "Sophia";
    /*
     * Agent ka apna avatar — is screen pe teen jagah lagta hai (Meet dialog ka
     * bada profile, "is ready" header, aur cost-compare card). Pehle teeno pe
     * "/svg/receptionist_profile.png" hardcoded tha, isliye har agent ek hi
     * generic tasveer dikhata tha chahe uska avatar Female-01 ho ya Female-05.
     *
     * Ek hi jagah resolve karke teeno me bhejte hain, warna aage koi ek jagah
     * badalna bhool jaata hai (abhi tak yahi ho raha tha).
     */
    const agentAvatarSrc =
        agentData?.agent?.avatar || agentData?.agent?.agentAvatar || "/svg/receptionist_profile.png";

    // Dashboard ke "Test Agent Call" se aane par (suppressPopup) na "Meet"
    // dialog khulta hai aur na auto-start chalta hai — to koi call shuru hi
    // nahi hoti, globalCallStatus "idle" rehta hai, aur banner render hi nahi
    // hota (GlobalCallBanner idle par null lautata hai). User ready summary pe
    // pahunchta hai jahan call chalane ka koi button hi nahi.
    //
    // Isliye banner ko "prime" kar dete hain: call band hi rehti hai, par
    // banner apne green button ke saath dikhta hai taaki user wahin se call
    // shuru kar sake. Ye call START nahi karta.
    //
    // NOTE: `suppressPopup` gate deliberately kept — priming on EVERY agent-ready
    // load set globalCallStatus to "ready", which the "Make Web Call Now" button's
    // in-progress check treated as a live call and DISABLED the button until a
    // refresh (both Retell + Python). Gating to the dashboard/suppressPopup path
    // restores the pre-change behaviour where the card button works normally.
    const primedBannerRef = useRef(false);
    useEffect(() => {
        if (!location.state?.suppressPopup) return;
        if (primedBannerRef.current) return;
        const agentId = agentData?.agent?.agent_id;
        if (!agentId) return; // agentData abhi load nahi hui — agle render pe
        primedBannerRef.current = true;
        primeGlobalCall({
            agentId,
            agentName: agentDisplayName,
            agentAvatar: agentData?.agent?.avatar || agentData?.agent?.agentAvatar || "/svg/receptionist_profile.png",
            businessName: businessNameDisplay,
        });
    }, [
        location.state?.suppressPopup,
        agentData?.agent?.agent_id,
        agentDisplayName,
        businessNameDisplay,
        primeGlobalCall,
    ]);

    // On the native app the "Meet Sophia" dialog must mirror the GLOBAL call
    // (which auto-starts), not the unused local client — so it shows
    // Connecting → live wave → End correctly instead of a stale "Make Web Call".
    const dispCallConnected = isMobileApp
        ? globalCallStatus === "active"
        : isCallConnected;
    const dispCallLoading = isMobileApp
        ? globalCallStatus === "connecting"
        : callLoading;
    const dispIsEnding = isMobileApp ? globalCallStatus === "ending" : isEnding;
    // Call ban rahi hai par abhi juri nahi — button "Connecting..." dikhata hai.
    const isConnecting = dispCallLoading && !dispCallConnected;
    // Show the 9b "ready" summary (with the live waveform widget) once a call is
    // connected too — not only after it has completed — so when the web popup
    // closes on connect there's a live view behind it (matches native).
    const showReadyView = hasCompletedCall || isMobileApp || dispCallConnected;
    const callLiveNow =
        globalCallStatus === "active" ||
        globalCallStatus === "connecting" ||
        globalCallStatus === "ending";
    // API count OR the reload-safe sessionStorage fallback (bumped on call end).
    const hasPriorCalls =
        (priorCallCount || 0) > 0 || getSessionCallCount(agentID) > 0;
    // "Go Live" footer ab HAMESHA dikhta hai. Pehle ye
    //   !isMobileApp || !callLiveNow || hasCompletedCall || hasPriorCalls
    // tha — yaani user ki SABSE PEHLI live call ke dauraan button chhupa rehta
    // tha. Ab wahi gating hata di gayi jaisi $5 / $20 / plan-popup buttons se
    // hatai hai: user ko aage badhne ka rasta turant dikhna chahiye.
    const showGoLiveFooter = true;
    // Wave bounces ONLY while the agent is speaking (native → global provider,
    // web → local Retell talking events).
    const agentTalking = isMobileApp ? globalAgentTalking : webAgentTalking;

    // Web: once the call actually connects, close the "Meet" popup so it
    // collapses into the ready summary + live widget (waveform pill + red End
    // Call), like the native banner. (Native closes via the sales flow instead.)

    // 2. When a live call is cancelled or ended from GlobalCallBanner, close the popup & reveal summary.
    useEffect(() => {
        const prev = prevGlobalStatusRef.current;
        prevGlobalStatusRef.current = globalCallStatus;

        if (isReloadingRef.current) return;

        // "ended" too: when the AGENT ends the call (e.g. the caller says
        // "disconnect"), the context settles on "ended", never "idle" — the
        // popup stayed open over a call that was already gone.
        if (
            (prev === "active" || prev === "connecting" || prev === "ending") &&
            (globalCallStatus === "idle" || globalCallStatus === "ended")
        ) {
            setShowCallDialog(false);
            setHasCompletedCall(true);
        }
    }, [globalCallStatus]);

    // Smooth the raw talking flag: turn the wave ON instantly when the agent
    // starts, but linger ~650ms before turning OFF so short between-word pauses
    // don't make it stutter.
    useEffect(() => {
        if (agentTalking) {
            setWaveOn(true);
            return;
        }
        const t = setTimeout(() => setWaveOn(false), 650);
        return () => clearTimeout(t);
    }, [agentTalking]);

    // Owner satisfied (promoted) or an offer triggered → close the popup so the
    // banner shows on this same screen (the "ready" view + banner).
    useEffect(() => {
        if (promoted || forcedOffer) setShowCallDialog(false);
    }, [promoted, forcedOffer]);

    // silent_mode tool fired (user asked the agent to go quiet) → close the
    // popup. The call keeps running (banner/live widget); the agent stays silent
    // on its own until the user speaks again. No resume nudge here — going quiet
    // is the whole point. Gated on silentNonce so a repeat request re-closes it.
    useEffect(() => {
        if (!silentNonce) return;
        setShowCallDialog(false);
    }, [silentNonce]);

    // After the satisfaction check (promote) the popup collapses to the "ready"
    // screen — and because satisfaction_level has speak_after_execution:false,
    // the agent can go SILENT here. Inject a one-time nudge so it RESUMES the
    // same conversation (moves into Node 2) instead of going quiet.
    useEffect(() => {
        if (!isMobileApp) return;
        if (!promoted) return;
        if (globalCallStatus !== "active") return;
        if (resumeInjectedRef.current) return;
        resumeInjectedRef.current = true;
        injectContext(
            "CONTEXT: The customer just moved to the 'you're ready' summary and may have gone quiet after your last action. Continue the SAME conversation naturally — do NOT restart, do NOT re-introduce yourself, do NOT repeat your greeting. Pick up right where you left off and move into Node 2: in ONE short, warm sentence, ask how many customer calls they usually get in a day or week."
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobileApp, promoted, globalCallStatus]);

    /*
     * Auto-start disabled per user request: call will only start when the user
     * manually taps the green "Make Web Call Now" button.
     */
    // useEffect(() => {
    //     if (!isMobileApp) return;
    //     if (globalAutoStartedRef.current) return;
    //     if (globalCallStatus !== "idle") return;
    //     const agentId = agentData?.agent?.agent_id;
    //     if (!agentId) return;
    //
    //     globalAutoStartedRef.current = true;
    //     startGlobalCall({
    //         agentId,
    //         agentName: agentDisplayName,
    //         agentAvatar: agentData?.agent?.avatar || agentData?.agent?.agentAvatar || "/svg/receptionist_profile.png",
    //         businessName: businessNameDisplay,
    //     });
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [isMobileApp, agentData?.agent?.agent_id, globalCallStatus]);

    return (
        <div className={styles.containerBox}>
            {dashboardLoading && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(255,255,255,0.7)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999,
                    }}
                >
                    <Loader2 />
                </div>
            )}
            {/* <div className={styles.Forsticky}>
                <HeaderBar title='' hideBackButton />
            </div> */}

            {/*
              Page body — Figma 9b post-call summary. Visible only after
              the call dialog has been dismissed AND a call has been
              completed. While the dialog is open, the body stays empty
              (just the sticky header) so the dialog dominates the view
              without competing chrome behind it.
            */}
            <div className={styles.container}>
                {showReadyView && (
                    <div className={styles.readyView}>
                        {/* Header: avatar + ready text */}
                        <div className={styles.readyHeader}>
                            <div className={styles.readyAvatar} aria-hidden="true">
                                <img
                                    src={agentAvatarSrc}
                                    alt=""
                                    onError={(e) => {
                                        e.currentTarget.src = "/svg/receptionist_profile.png";
                                    }}
                                />
                                <img src="/svg/verified 1.svg" alt="" className={styles.readyAvatarBadge} />
                            </div>
                            <div className={styles.readyHeaderText}>
                                <h2 className={styles.readyTitle}>{agentDisplayName} is ready</h2>
                                <p className={styles.readyFor}>
                                    for{" "}
                                    {businessNameDisplay.length > 15 ? (
                                        <span
                                            className={`${styles.bizName} ${showBizTip ? styles.bizTipOpen : ""}`}
                                            role="button"
                                            tabIndex={0}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowBizTip(true);
                                                clearTimeout(bizTipTimer.current);
                                                bizTipTimer.current = setTimeout(() => setShowBizTip(false), 2000);
                                            }}
                                        >
                                            {businessNameDisplay.slice(0, 15)}…
                                            <span className={styles.bizTooltip}>{businessNameDisplay}</span>
                                        </span>
                                    ) : (
                                        <span>{businessNameDisplay}</span>
                                    )}
                                </p>
                            </div>
                        </div>


                        {/* Divider heading */}
                        <div className={styles.didDivider}>
                            <span className={styles.didLine} />
                            <span className={styles.didText}>Here's what she just did:</span>
                            <span className={styles.didLine} />
                        </div>

                        {/* Checklist */}
                        <ul className={styles.didList}>
                            <li>
                                <span className={styles.didCheck} aria-hidden="true">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" fill="#22C55E" />
                                        <path d="M8 12l2.5 2.5L16 9" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                                Answered in 1.2 seconds - no voicemail, no lost customer
                            </li>
                            <li>
                                <span className={styles.didCheck} aria-hidden="true">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" fill="#22C55E" />
                                        <path d="M8 12l2.5 2.5L16 9" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                                Ready to book appointments into your calendar
                            </li>
                            <li>
                                <span className={styles.didCheck} aria-hidden="true">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" fill="#22C55E" />
                                        <path d="M8 12l2.5 2.5L16 9" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                                Blocks spam free - you never pay for junk
                            </li>
                        </ul>

                        {/* Comparison card */}
                        <div className={styles.compareCard}>
                            <p className={styles.compareTop}>
                                Traditional Full-Time Receptionist:{' '}
                                <span className={styles.compareStrike}>$3,500+</span>
                                <span className={styles.compareStrikeUnit}> /month</span>
                            </p>
                            <div className={styles.compareBottom}>
                                <img
                                    src={agentAvatarSrc}
                                    alt=""
                                    className={styles.compareAvatar}
                                    aria-hidden="true"
                                    onError={(e) => {
                                        e.currentTarget.src = "/svg/receptionist_profile.png";
                                    }}
                                />
                                <p>
                                    <strong>{agentDisplayName}</strong> works 24/7 for{' '}
                                    <strong>far less cost</strong>
                                </p>
                            </div>
                        </div>

                        {/* Lightbulb note */}
                        <div className={styles.bulbNote}>
                            <span className={styles.bulbIcon} aria-hidden="true">
                                <img src="/svg/bulb_icon.svg" alt="Lightbulb" />
                            </span>
                            <p>
                                 Every call she catches is a typical <strong>$250 customer</strong> you'd have missed.
                            </p>
                        </div>

                    </div>
                )}
            </div>

            {/* Go Live — sticky at the bottom, outside the scrolling summary.
                Native app has no local call dialog, so show it right away (the
                call runs live in the global banner); web keeps the after-call gate. */}
            {!dialogRender && showReadyView && showGoLiveFooter && (
                <div className={styles.readyFooter}>
                    <AnimatedButton2
                        label={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                Go Live with {agentDisplayName} <span aria-hidden="true"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M7.33944 13.093L5.94953 11.7166L10.1125 7.55362H-0.00146484V5.54297H10.1125L5.94953 1.38672L7.33944 0.00355083L13.8842 6.5483L7.33944 13.093Z" fill="white"/>
</svg>
</span>
                            </span>
                        }
                        variant="purple"
                        onClick={handleSeePlans}
                        positionType="relative"
                        bottom="20px"
                    />
                </div>
            )}


            {dialogRender && (
                <div
                    className={`${styles.dialogBackdrop} ${dialogClosing ? styles.backdropClosing : ""}`}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Test call with Sophia"
                >
                    <div
                        className={`${styles.dialogCard} ${dialogClosing ? styles.cardClosing : ""}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close (×) — WEB only. The native-app "Meet" popup is
                            non-dismissible (it collapses into the banner once the
                            sales agent detects satisfaction). */}
                        {!isMobileApp && (
                            <button
                                type="button"
                                className={styles.dialogClose}
                                onClick={handleDialogClose}
                                aria-label="Close"
                                disabled={isCallInProgress}
                                style={{
                                    cursor: isCallInProgress ? 'not-allowed' : 'pointer',
                                    opacity: isCallInProgress ? 0.4 : 1,
                                    pointerEvents: isCallInProgress ? 'none' : 'auto',
                                }}
                            >
                                ×
                            </button>
                        )}

                        <div className={styles.titleBar}>
                            {isDataLoading ? (
                                <>
                                    <div className={styles.skeletonLine} style={{ width: '120px', height: '32px', marginBottom: '8px' }} />
                                    <br />
                                    <div className={styles.skeletonLine} style={{ width: '220px', height: '22px' }} />
                                    <p className={styles.subtitle}>
                                        <span className={styles.skeletonLine} style={{ width: '80%', height: '16px' }} />
                                        <span className={styles.skeletonLine} style={{ width: '60%', height: '16px', marginTop: '6px' }} />
                                    </p>
                                </>
                            ) : (
                                <>
                                    <h4 className={styles.lightTitle}>
                                        Meet {agentDisplayName}
                                    </h4>
                                    <h5 className={styles.boldTitle}>
                                        Built for <span>{businessNameDisplay}</span>
                                    </h5>
                                    <p className={styles.subtitle}>
                                        Call <strong className={styles.boldSub}>{agentDisplayName}</strong> now and hear how she answers for{" "}
                                        <strong className={styles.boldSub}>{businessNameDisplay}</strong>.
                                        This is exactly how your customers will experience her.
                                    </p>
                                </>
                            )}
                        </div>

                        <div className={styles.ProfileBar}>
                            <div className={styles.profileWrap}>
                                <div className={styles.imgWrap}>
                                    {isDataLoading ? (
                                        <div className={styles.skeletonCircle} />
                                    ) : (
                                        <img
                                            src={agentAvatarSrc}
                                            alt={agentDisplayName}
                                            onError={(e) => {
                                                // Avatar file na mile to generic
                                                // tasveer — toota image icon nahi.
                                                e.currentTarget.src =
                                                    "/svg/receptionist_profile.png";
                                            }}
                                        />
                                    )}
                                </div>
                            </div>

                            {dispCallConnected && (
                                <CallWaveform />
                            )}
                        </div>

                        <div className={styles.NameBar} style={{ borderBottom: isEditing || isDataLoading ? 'none' : '1px solid' }}>
                            {isDataLoading ? (
                                <span className={styles.skeletonLine} style={{ width: '120px', height: '24px', marginBlock: '8px' }} />
                            ) : isEditing ? (
                                <input
                                    ref={inputRef}
                                    value={name}
                                    maxLength={14}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        const filtered = raw
                                            .replace(/[0-9]/g, '')
                                            .replace(/[^a-zA-Z\s]/g, '');
                                        if (filtered.length <= 14) {
                                            setName(filtered);
                                        }
                                    }}
                                    onBlur={handleBlur}
                                    className={styles.input}
                                    disabled={isCallInProgress}
                                />
                            ) : (
                                <>
                                    <span className={styles.name}>{agentDisplayName}</span>
                                    <img
                                        src="/svg/edit_svg_new.svg"
                                        alt="edit-svg.svg"
                                        className={styles.icon}
                                        onClick={handleEditClick}
                                        style={{
                                            cursor: isCallInProgress ? 'not-allowed' : 'pointer',
                                            opacity: isCallInProgress ? 0.5 : 1,
                                            pointerEvents: isCallInProgress ? 'none' : 'auto'
                                        }}
                                    />
                                </>
                            )}
                        </div>
                        {isDataLoading ? (
                            <span className={styles.skeletonLine} style={{ width: '180px', height: '12px', marginTop: '6px' }} />
                        ) : (
                            <p className={styles.nameDesc}>You can <span className={styles.boldText2}>rename '{agentDisplayName}'</span> as per your choice</p>
                        )}

                        {isDataLoading ? (
                            <div className={styles.skeletonLine} style={{ width: '100%', height: '48px', borderRadius: '24px', marginTop: '12px' }} />
                        ) : (
                            <>
                                {/* Pre-call CTA — green pill */}
                                {!dispCallConnected && !dispCallLoading && (
                                    <AnimatedButton2
                                        label={
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <img src="/svg/white_call_icon.svg" alt="white_call_icon" aria-hidden="true" style={{ width: '18px', height: '18px' }} />
                                                Make Web Call Now <span aria-hidden="true">→</span>
                                            </span>
                                        }
                                        variant="green"
                                        onClick={handleStartCall}
                                        disabled={isCallInProgress}
                                        positionType="relative"
                                        bottom={null}
                                    />
                                )}

                                {/* In-call button. Connecting / Ending / live —
                                    teeno states isi ek button ke andar dikhte
                                    hain. Pehle "Connecting..." iske UPAR ek alag
                                    block me aata tha, to connect hote waqt text
                                    aur button dono ek saath dikhte the aur
                                    connect hone par layout khisak jata tha. */}
                                {(dispCallConnected || dispCallLoading) && (
                                    // Pre-call green pill ki tarah ab yahan bhi wahi
                                    // AnimatedButton2 hai — bas red variant me, taki
                                    // dono CTA ka shape/animation ek jaisa rahe.
                                    <AnimatedButton2
                                        label={
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <img src="/svg/phone_cut_icon2.svg" alt="" aria-hidden="true" style={{ width: '18px', height: '18px' }} />
                                                {isConnecting
                                                    ? 'Connecting...'
                                                    : isEnding
                                                        ? 'Ending...'
                                                        : <>End Call Now <span aria-hidden="true">→</span></>}
                                            </span>
                                        }
                                        variant="red"
                                        onClick={handleEndCallWithFarewell}
                                        // Connect hone tak call cut karne ko kuch hai hi nahi.
                                        disabled={isEnding || isConnecting}
                                        positionType="relative"
                                        bottom={null}
                                    />
                                )}
                            </>
                        )}
                        <p className={styles.micPermissionHint}>
                            Need MIC permissions, if not granted
                        </p>
                    </div>
                </div>
            )}

            {/* Floating "Call ended" toast — real phone jaisa, layout shift nahi */}
            {showCallEndedNote && (
                <div className={styles.callEndedToast} role="status" aria-live="polite">
                    <img src="/svg/phone_cut_icon2.svg" alt="" aria-hidden="true" />
                    <span>Call ended</span>
                </div>
            )}

            {/* In-app permission / error popup — replaces native alert() */}
            {popupMessage && (
                <PopUp
                    type={popupType}
                    message={popupMessage}
                    onClose={() => setPopupMessage("")}
                />
            )}

            {/* "Can we please keep talking?" before ending from the live widget. */}
            <KeepTalkingModal
                isOpen={showEndConfirm}
                onKeepTalking={() => setShowEndConfirm(false)}
                onEndCall={() => {
                    setShowEndConfirm(false);
                    // Native ends the GLOBAL call; web ends the LOCAL client call.
                    if (isMobileApp) endGlobalCall();
                    else handleEndCall();
                }}
            />
        </div>
    );
};

export default FastSetup;
